'use client';

import classnames from 'classnames';
import React, { useState } from 'react';

import { ShopLinkType } from '@cineamo/api/models/website-configuration/ShopLink.types';

import { getExternalLinkTarget } from '@/helpers/external-link-helper';

import { DROPDOWN_MENU_ITEM_CLASSNAME } from '@cineamo/ui/src/dropdown/dropdown-colors';
import DropdownPanel from '@cineamo/ui/src/dropdown/DropdownPanel';
import { IconPopcornBag24 } from '@cineamo/ui/src/icons/ic-popcornBag';
import { IconProfile24 } from '@cineamo/ui/src/icons/ic-profile';
import { IconShoppingBag24 } from '@cineamo/ui/src/icons/ic-shoppingBag';
import { IconVoucher24 } from '@cineamo/ui/src/icons/ic-voucher';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ShopDropdownItem = {
    href: string;
    title: string;
    type: ShopLinkType;
};

type ShopLinksDropdownProps = {
    items: ShopDropdownItem[];
    triggerTitle: string;
};

function ShopLinkIcon({ type }: { type: ShopLinkType }): React.JSX.Element {
    if (type === 'snack-shop') return <IconPopcornBag24 />;
    if (type === 'login') return <IconProfile24 />;
    return <IconVoucher24 />;
}

function ShopLinksDropdown({ items, triggerTitle }: ShopLinksDropdownProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const externalLinkTarget = getExternalLinkTarget(domainConfiguration?.websiteConfiguration);

    const triggerStyle = {
        // Highlight the trigger like an active nav item while the dropdown is open.
        color: isPanelOpen ? domainConfiguration.style.onBackgroundColor : undefined,
        opacity: isPanelOpen ? 1 : 0.7
    };

    return (
        <div
            className="relative"
            role="menu"
            tabIndex={0}
            onFocus={() => setIsPanelOpen(true)}
            onMouseEnter={() => setIsPanelOpen(true)}
            onMouseLeave={() => setIsPanelOpen(false)}>
            <div
                className={classnames(
                    'cursor-pointer',
                    'text-body-bold-14',
                    'flex flex-row flex-none space-x-8 items-center grow-0',
                    'h-48 py-8 px-[20px]'
                )}
                style={triggerStyle}>
                <IconShoppingBag24 />
                <span>{triggerTitle}</span>
            </div>
            {isPanelOpen && (
                /* The gap to the trigger is this wrapper's transparent padding, so the pointer
                   never leaves the menu while crossing it — and unlike the `w-full` spacer this
                   replaces, the bridge is as wide as the panel rather than as wide as the trigger.
                   See `NavigationItemMenu` for the full account. */
                <div className="absolute top-full right-0 z-[60] pt-10">
                    <DropdownPanel className="flex flex-col rounded-8">
                        {items.map((item) => {
                            const isEmbedded = item.href.startsWith('/embedded/');
                            return (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    target={isEmbedded ? undefined : externalLinkTarget.target}
                                    rel={isEmbedded ? undefined : externalLinkTarget.rel}
                                    onClick={() => {
                                        setIsPanelOpen(false);
                                        if (item.type === 'voucher') {
                                            track('Voucher Link Clicked');
                                        }
                                    }}
                                    className={classnames(
                                        'flex flex-row items-center space-x-8 py-8 px-16 text-body-14 whitespace-nowrap',
                                        DROPDOWN_MENU_ITEM_CLASSNAME
                                    )}>
                                    <ShopLinkIcon type={item.type} />
                                    <span>{item.title}</span>
                                </Link>
                            );
                        })}
                    </DropdownPanel>
                </div>
            )}
        </div>
    );
}

export default ShopLinksDropdown;
